import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  ComposedChart,
} from 'recharts';
import { History, Trash2, TrendingUp, TrendingDown, Minus, Scale, Activity } from 'lucide-react';
import { getHistory, deleteRecord, clearHistory, getTrendData, getStatistics } from '@/lib/storage';
import { formatBMI } from '@/lib/bmi-utils';
import { toast } from 'sonner';

export const HistoryPanel = ({ refreshTrigger }) => {
  const [history, setHistory] = useState(() => getHistory());
  const [trendData, setTrendData] = useState(() => getTrendData());
  const [statistics, setStatistics] = useState(() => getStatistics());

  const loadData = useCallback(() => {
    setHistory(getHistory());
    setTrendData(getTrendData());
    setStatistics(getStatistics());
  }, []);

  useEffect(() => {
    loadData();
  }, [refreshTrigger, loadData]);

  const handleDeleteRecord = (id) => {
    if (deleteRecord(id)) {
      loadData();
      toast.success('Record deleted');
    } else {
      toast.error('Failed to delete record');
    }
  };

  const handleClearHistory = () => {
    if (clearHistory()) {
      loadData();
      toast.success('History cleared');
    } else {
      toast.error('Failed to clear history');
    }
  };

  const getCategoryBadgeClass = (category) => {
    if (category?.includes('Underweight')) return 'badge-underweight';
    if (category?.includes('Normal')) return 'badge-normal';
    if (category?.includes('Overweight')) return 'badge-overweight';
    if (category?.includes('Obese')) return 'badge-obese';
    return '';
  };

  const getTrendIcon = () => {
    if (!statistics) return <Minus className="w-4 h-4" />;
    if (statistics.trend === 'increasing') return <TrendingUp className="w-4 h-4 text-warning" />;
    if (statistics.trend === 'decreasing') return <TrendingDown className="w-4 h-4 text-success" />;
    return <Minus className="w-4 h-4 text-muted-foreground" />;
  };

  if (history.length === 0) {
    return (
      <Card className="card-elevated">
        <CardContent className="py-12">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-4">
              <History className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">No History Yet</h3>
            <p className="text-muted-foreground text-sm max-w-sm mx-auto">
              Calculate your BMI and save results to track your progress over time.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Statistics Overview */}
      {statistics && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="card-elevated">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground mb-1">Avg BMI</p>
              <p className="text-2xl font-bold text-foreground">{statistics.avgBmi}</p>
              <div className="flex items-center justify-center gap-1 mt-1">
                {getTrendIcon()}
                <span className="text-xs text-muted-foreground capitalize">{statistics.trend}</span>
              </div>
            </CardContent>
          </Card>
          <Card className="card-elevated">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground mb-1">Avg Weight</p>
              <p className="text-2xl font-bold text-foreground">{statistics.avgWeight}</p>
              <p className="text-xs text-muted-foreground mt-1">kg</p>
            </CardContent>
          </Card>
          <Card className="card-elevated">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground mb-1">BMI Range</p>
              <p className="text-2xl font-bold text-foreground">
                {statistics.minBmi}-{statistics.maxBmi}
              </p>
              <p className="text-xs text-muted-foreground mt-1">min-max</p>
            </CardContent>
          </Card>
          <Card className="card-elevated">
            <CardContent className="p-4 text-center">
              <p className="text-xs text-muted-foreground mb-1">Records</p>
              <p className="text-2xl font-bold text-foreground">{statistics.totalRecords}</p>
              <p className="text-xs text-muted-foreground mt-1">saved</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Trend Charts */}
      {trendData.length > 1 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* BMI Trend Chart */}
          <Card className="card-elevated">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="w-4 h-4 text-primary" />
                BMI Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={trendData}>
                    <defs>
                      <linearGradient id="bmiGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 11 }} 
                      className="text-muted-foreground"
                    />
                    <YAxis 
                      domain={['auto', 'auto']} 
                      tick={{ fontSize: 11 }}
                      className="text-muted-foreground"
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="bmi"
                      stroke="hsl(var(--primary))"
                      fill="url(#bmiGradient)"
                    />
                    <Line
                      type="monotone"
                      dataKey="bmi"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                      dot={{ fill: 'hsl(var(--primary))', strokeWidth: 2 }}
                      activeDot={{ r: 6, fill: 'hsl(var(--primary))' }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Weight Trend Chart */}
          <Card className="card-elevated">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Scale className="w-4 h-4 text-accent" />
                Weight Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 11 }}
                      className="text-muted-foreground"
                    />
                    <YAxis 
                      domain={['auto', 'auto']} 
                      tick={{ fontSize: 11 }}
                      className="text-muted-foreground"
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                      formatter={(value) => [`${value.toFixed(1)} kg`, 'Weight']}
                    />
                    <Bar
                      dataKey="weight"
                      fill="hsl(var(--accent))"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* History Table */}
      <Card className="card-elevated">
        <CardHeader className="flex flex-row items-center justify-between pb-4 flex-wrap gap-2">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <History className="w-5 h-5 text-primary" />
              Calculation History
            </CardTitle>
            <CardDescription className="hidden sm:block">Your saved BMI calculations</CardDescription>
          </div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" size="sm" className="text-destructive hover:text-destructive">
                <Trash2 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Clear All</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear all history?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete all your saved BMI calculations. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleClearHistory}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  Clear All
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </CardHeader>
        <CardContent className="p-0 sm:p-6 sm:pt-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Date</TableHead>
                  <TableHead>BMI</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="hidden sm:table-cell">Weight</TableHead>
                  <TableHead className="hidden md:table-cell">Mode</TableHead>
                  <TableHead className="w-[40px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.slice(0, 10).map((record) => (
                  <TableRow key={record.id}>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(record.date).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric'
                      })}
                    </TableCell>
                    <TableCell className="font-semibold">{formatBMI(record.bmi)}</TableCell>
                    <TableCell>
                      <Badge className={`${getCategoryBadgeClass(record.whoCategory)} text-xs`}>
                        {record.whoCategory}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm hidden sm:table-cell">{record.weight.toFixed(1)} kg</TableCell>
                    <TableCell className="hidden md:table-cell">
                      {record.athleteMode && (
                        <Badge variant="outline" className="text-xs">
                          <Activity className="w-3 h-3 mr-1" />
                          Athlete
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteRecord(record.id)}
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HistoryPanel;
